import type { FC, SVGProps } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Sparkles,
  Compass,
  Building2,
  Home,
  BarChart3,
  Network,
  CalendarDays,
  Map as MapIcon,
  Search,
  Flag,
  FileText,
  FolderOpen,
  Bell,
  Target,
} from "lucide-react";
import {
  DashboardGlyph,
  SubscriptionsGlyph,
  WealthScopeGlyph,
  NetWorthGlyph,
  CashFlowGlyph,
  BuyingPowerGlyph,
  DebtToIncomeGlyph,
} from "./appCustomIcons";

export type AppIcon = LucideIcon | FC<SVGProps<SVGSVGElement>>;

export type TutorialLayout =
  | "dashboard"
  | "wealth-hero"
  | "cash-flow"
  | "metric-grid"
  | "property-list"
  | "performance"
  | "entities"
  | "calendar"
  | "hunt"
  | "map"
  | "pipeline"
  | "analytics"
  | "reports"
  | "documents"
  | "alerts"
  | "subscriptions";

export type TutorialSceneMetric = { label: string; value: string };

export type TutorialScene = {
  navLabel: string;
  pageTitle: string;
  headline: string;
  narration: string;
  layout: TutorialLayout;
  bullets: string[];
  metrics?: TutorialSceneMetric[];
  path?: string;
  pathLabel?: string;
  durationSec?: number;
};

export type TutorialModule = {
  id: string;
  name: string;
  icon: AppIcon;
  sidebarTitle: string;
  scenes: TutorialScene[];
};

/* ─── Scene → icon map, mirrors the real app's NAV_ICONS table ── */
export const NAV_ICONS: Record<string, AppIcon> = {
  Dashboard: DashboardGlyph,
  "Net Worth": NetWorthGlyph,
  "Cash Flow": CashFlowGlyph,
  "Buying Power": BuyingPowerGlyph,
  "Debt-to-Income": DebtToIncomeGlyph,
  "Milestone Tracker": Flag,
  Properties: Home,
  Performance: BarChart3,
  Entities: Network,
  "Lease Calendar": CalendarDays,
  Hunt: MapIcon,
  Discover: Search,
  "Deal Map": MapIcon,
  "My Pipeline": Compass,
  Analytics: BarChart3,
  "My Subscriptions": SubscriptionsGlyph,
  "Reports & Data": FileText,
  Documents: FolderOpen,
  "Alerts & Insights": Bell,
};

export function sceneIcon(navLabel: string): AppIcon {
  return NAV_ICONS[navLabel] ?? Home;
}

/* ─── Ported from vawena-frontend-webapp's tutorialModules.ts ── */
export const TUTORIAL_MODULES: TutorialModule[] = [
  {
    id: "getting-started",
    name: "Get Started",
    icon: Sparkles,
    sidebarTitle: "VaWeNa",
    scenes: [
      {
        navLabel: "Dashboard",
        pageTitle: "Dashboard",
        headline: "Pick your module from home",
        narration: "After sign-in you land on Dashboard. Module cards open each product.",
        layout: "dashboard",
        bullets: ["Plan badge in the hero", "Module cards are clickable entry points", "Finish onboarding from amber banners"],
        path: "/dashboard",
        pathLabel: "Open Dashboard",
        durationSec: 5,
      },
      {
        navLabel: "My Subscriptions",
        pageTitle: "My Subscriptions",
        headline: "Choose or upgrade a plan",
        narration: "Upgrade to WealthScope, PortfolioIQ, or the bundle and manage billing here.",
        layout: "subscriptions",
        bullets: ["Current plan and renewal date", "Upgrade to modules or the bundle", "Cancel anytime; access lasts through the period"],
        path: "/subscriptions",
        pathLabel: "Open Subscriptions",
        durationSec: 5,
      },
      {
        navLabel: "Reports & Data",
        pageTitle: "Reports & Data",
        headline: "Generate exports in minutes",
        narration: "Pick a module, report type, date range, and format — then download.",
        layout: "reports",
        bullets: ["FileText icon in shared sidebar tools", "Supported types depend on your modules", "Download when the job completes"],
        path: "/reports",
        pathLabel: "Open Reports",
        durationSec: 5,
      },
      {
        navLabel: "Documents",
        pageTitle: "Documents",
        headline: "Store statements & closing files",
        narration: "Upload PDFs, CSVs, and spreadsheets so statements stay with your account.",
        layout: "documents",
        bullets: ["FolderOpen icon in the sidebar", "Upload closing packages and statements", "Reuse files when generating reports"],
        path: "/documents",
        pathLabel: "Open Documents",
        durationSec: 5,
      },
      {
        navLabel: "Alerts & Insights",
        pageTitle: "Alerts & Insights",
        headline: "Catch what needs attention",
        narration: "Alerts surface risks and opportunities across modules.",
        layout: "alerts",
        bullets: ["Bell icon for Alerts & Insights", "Filter by severity inside the card", "Configure digests in Settings"],
        path: "/alerts",
        pathLabel: "Open Alerts",
        durationSec: 5,
      },
    ],
  },
  {
    id: "overall",
    name: "Overall App",
    icon: Compass,
    sidebarTitle: "VaWeNa",
    scenes: [
      {
        navLabel: "Dashboard",
        pageTitle: "Dashboard",
        headline: "Your home for every module",
        narration: "After sign-in you land on Dashboard. Module cards open WealthScope, PortfolioIQ, and DealHunterIQ.",
        layout: "dashboard",
        bullets: ["Greeting header with your plan badge", "Module cards are the main entry points", "Onboarding banners appear until setup is done"],
        metrics: [
          { label: "Modules", value: "3" },
          { label: "Alerts", value: "2" },
          { label: "Setup", value: "Ready" },
        ],
        path: "/dashboard",
        pathLabel: "Open Dashboard",
        durationSec: 6,
      },
      {
        navLabel: "Net Worth",
        pageTitle: "Net Worth",
        headline: "WealthScope — assets minus liabilities",
        narration: "Net Worth opens with a brand-blue hero: total net worth, assets, liabilities, and monthly income.",
        layout: "wealth-hero",
        bullets: ["Blue hero banner matches live WealthScope pages", "Add income, assets, or liabilities from the header", "Trend chart shows your 12-month net worth"],
        metrics: [
          { label: "Net Worth", value: "$1.24M" },
          { label: "Assets", value: "$1.8M" },
          { label: "Liabilities", value: "$560K" },
        ],
        path: "/wealth-scope/net-worth",
        pathLabel: "Open Net Worth",
        durationSec: 6,
      },
      {
        navLabel: "Cash Flow",
        pageTitle: "Cash Flow",
        headline: "Income and expenses over time",
        narration: "Cash Flow breaks down money in vs out so you can see what drives monthly surplus.",
        layout: "cash-flow",
        bullets: ["Income vs expense categories in one view", "Linked accounts keep categories fresher", "Use breakdowns for deeper merchant detail"],
        metrics: [
          { label: "Income", value: "$18.4K" },
          { label: "Expenses", value: "$12.1K" },
          { label: "Net", value: "+$6.3K" },
        ],
        path: "/wealth-scope/cash_flow",
        pathLabel: "Open Cash Flow",
        durationSec: 5,
      },
      {
        navLabel: "Properties",
        pageTitle: "Properties",
        headline: "PortfolioIQ property roster",
        narration: "Properties lists your investments with value and status — add new ones or open a property for detail tabs.",
        layout: "property-list",
        bullets: ["Add Property or import from WealthScope", "Cards show address, value, and key metrics", "Open a property for occupancy and expenses"],
        metrics: [
          { label: "Properties", value: "12" },
          { label: "Units", value: "48" },
          { label: "Value", value: "$4.1M" },
        ],
        path: "/portfolio_iq/properties",
        pathLabel: "Open Properties",
        durationSec: 5,
      },
      {
        navLabel: "Performance",
        pageTitle: "Performance",
        headline: "Returns across the portfolio",
        narration: "Performance compares NOI, cash flow, and returns so underperformers stand out.",
        layout: "performance",
        bullets: ["Portfolio-level KPIs at the top", "Switch views with page-level pill tabs", "Generate reports from Reports & Data"],
        metrics: [
          { label: "NOI", value: "$186K" },
          { label: "Cash Flow", value: "$62K" },
          { label: "Avg Cap", value: "5.4%" },
        ],
        path: "/portfolio_iq/performance",
        pathLabel: "Open Performance",
        durationSec: 5,
      },
      {
        navLabel: "Hunt",
        pageTitle: "Hunt",
        headline: "DealHunterIQ opportunity search",
        narration: "Hunt runs targeted searches for deals that match your criteria and buying power.",
        layout: "hunt",
        bullets: ["Define filters that match your buy box", "Save matches into My Pipeline", "Pair with Deal Map for location context"],
        metrics: [
          { label: "Matches", value: "24" },
          { label: "New Today", value: "5" },
          { label: "Saved", value: "3" },
        ],
        path: "/dealhunter_ai/hunt",
        pathLabel: "Open Hunt",
        durationSec: 5,
      },
      {
        navLabel: "Reports & Data",
        pageTitle: "Reports & Data",
        headline: "Export WealthScope or PortfolioIQ",
        narration: "Pick module, report type, range, and format — then generate and download.",
        layout: "reports",
        bullets: ["Module selector drives available report types", "PDF and CSV formats depending on access", "Store source files in Documents"],
        path: "/reports",
        pathLabel: "Open Reports",
        durationSec: 5,
      },
      {
        navLabel: "Alerts & Insights",
        pageTitle: "Alerts & Insights",
        headline: "What needs attention now",
        narration: "Alerts surface risks and opportunities across modules — severity filters help you triage.",
        layout: "alerts",
        bullets: ["Severity filter as an in-card segmented control", "Critical items rise to the top", "Tune email prefs in Settings → Notifications"],
        path: "/alerts",
        pathLabel: "Open Alerts",
        durationSec: 5,
      },
    ],
  },
  {
    id: "wealthscope",
    name: "WealthScope",
    icon: WealthScopeGlyph,
    sidebarTitle: "WealthScope",
    scenes: [
      {
        navLabel: "Net Worth",
        pageTitle: "Net Worth",
        headline: "See assets, liabilities & trend",
        narration: "Open WealthScope → Net Worth. Linked accounts and manual assets roll into one net worth view with a 12-month trend.",
        layout: "wealth-hero",
        bullets: ["Brand-blue hero with net worth total", "Assets / liabilities / monthly income strip", "Breakdown tables for each section"],
        metrics: [
          { label: "Net Worth", value: "$1.24M" },
          { label: "Assets", value: "$1.8M" },
          { label: "Liabilities", value: "$560K" },
        ],
        path: "/wealth-scope/net-worth",
        pathLabel: "Open Net Worth",
        durationSec: 5,
      },
      {
        navLabel: "Cash Flow",
        pageTitle: "Cash Flow",
        headline: "Track income vs expenses",
        narration: "Cash Flow shows money in and out by category. Connected accounts keep the monthly picture up to date.",
        layout: "cash-flow",
        bullets: ["Monthly net cash position", "Income (emerald) vs expenses (amber)", "Category drill-downs available"],
        metrics: [
          { label: "Income", value: "$18.4K" },
          { label: "Expenses", value: "$12.1K" },
          { label: "Net", value: "+$6.3K" },
        ],
        path: "/wealth-scope/cash_flow",
        pathLabel: "Open Cash Flow",
        durationSec: 5,
      },
      {
        navLabel: "Buying Power",
        pageTitle: "Buying Power",
        headline: "Estimate purchase capacity",
        narration: "Buying Power estimates what you can afford next — useful before you underwrite a deal or refinance.",
        layout: "metric-grid",
        bullets: ["Zap icon marks Buying Power in the sidebar", "Uses your WealthScope balance sheet", "Pair with Debt-to-Income before offers"],
        metrics: [
          { label: "Buying Power", value: "$285K" },
          { label: "Cash Ready", value: "$94K" },
          { label: "Leverage", value: "1.8x" },
        ],
        path: "/wealth-scope/buying_power",
        pathLabel: "Open Buying Power",
        durationSec: 5,
      },
      {
        navLabel: "Debt-to-Income",
        pageTitle: "Debt-to-Income",
        headline: "Lender-style affordability",
        narration: "Debt-to-Income compares obligations to income. Upload supporting docs when prompted.",
        layout: "metric-grid",
        bullets: ["Scale icon in the WealthScope sidebar", "Add supporting documents when prompted", "Status badge shows healthy vs elevated DTI"],
        metrics: [
          { label: "DTI", value: "28%" },
          { label: "Monthly Debt", value: "$4.2K" },
          { label: "Status", value: "Healthy" },
        ],
        path: "/wealth-scope/debt_income",
        pathLabel: "Open Debt-to-Income",
        durationSec: 5,
      },
      {
        navLabel: "Milestone Tracker",
        pageTitle: "Milestone Tracker",
        headline: "Set goals that update with you",
        narration: "Create milestones so progress updates as WealthScope data changes.",
        layout: "metric-grid",
        bullets: ["Flag icon for Milestone Tracker", "Progress fills as balances change", "Good place for down-payment or NW goals"],
        metrics: [
          { label: "Active Goals", value: "3" },
          { label: "On Track", value: "2" },
          { label: "Next Due", value: "Sep" },
        ],
        path: "/wealth-scope/milestone-tracker",
        pathLabel: "Open Milestones",
        durationSec: 5,
      },
    ],
  },
  {
    id: "portfolioiq",
    name: "PortfolioIQ",
    icon: Building2,
    sidebarTitle: "PortfolioIQ",
    scenes: [
      {
        navLabel: "Properties",
        pageTitle: "Properties",
        headline: "Add or import properties",
        narration: "Start in Properties. Use Add Property or import real estate already logged in WealthScope.",
        layout: "property-list",
        bullets: ["Property cards with address and value", "Add Property opens the full setup modal", "Click through for property detail tabs"],
        metrics: [
          { label: "Properties", value: "12" },
          { label: "Units", value: "48" },
          { label: "Value", value: "$4.1M" },
        ],
        path: "/portfolio_iq/properties",
        pathLabel: "Open Properties",
        durationSec: 5,
      },
      {
        navLabel: "Performance",
        pageTitle: "Performance",
        headline: "Compare returns across the portfolio",
        narration: "Performance surfaces NOI, cash flow, and returns so you can spot winners quickly.",
        layout: "performance",
        bullets: ["KPI cards along the top", "Charts use the shared color palette", "Pill tabs switch analysis views"],
        metrics: [
          { label: "NOI", value: "$186K" },
          { label: "Cash Flow", value: "$62K" },
          { label: "Avg Cap", value: "5.4%" },
        ],
        path: "/portfolio_iq/performance",
        pathLabel: "Open Performance",
        durationSec: 5,
      },
      {
        navLabel: "Entities",
        pageTitle: "Entities",
        headline: "Organize ownership structures",
        narration: "Entities group LLCs and ownership so each property has a clear holding structure.",
        layout: "entities",
        bullets: ["Landmark icon in the PortfolioIQ menu", "Link properties to each entity", "Keeps legal ownership clear in reports"],
        metrics: [
          { label: "Entities", value: "4" },
          { label: "LLCs", value: "3" },
          { label: "Linked", value: "12" },
        ],
        path: "/portfolio_iq/entities",
        pathLabel: "Open Entities",
        durationSec: 5,
      },
      {
        navLabel: "Lease Calendar",
        pageTitle: "Lease Calendar",
        headline: "Plan renewals & vacancies",
        narration: "Lease Calendar shows upcoming renewals and gaps so you can act before a unit goes dark.",
        layout: "calendar",
        bullets: ["Month view of lease end dates", "Vacancy risk at a glance", "Jump back to the property for edits"],
        metrics: [
          { label: "Active Leases", value: "41" },
          { label: "Renewing 90d", value: "6" },
          { label: "Vacant", value: "2" },
        ],
        path: "/portfolio_iq/lease-calendar",
        pathLabel: "Open Lease Calendar",
        durationSec: 5,
      },
    ],
  },
  {
    id: "dealhunter",
    name: "DealHunterIQ",
    icon: Target,
    sidebarTitle: "DealHunterIQ",
    scenes: [
      {
        navLabel: "Hunt",
        pageTitle: "Hunt",
        headline: "Search opportunities that fit you",
        narration: "Hunt lets you run targeted searches for deals that match your criteria.",
        layout: "hunt",
        bullets: ["Workspace with filters and results", "Save candidates for later diligence", "Tied to your buying-power context"],
        metrics: [
          { label: "Matches", value: "24" },
          { label: "New Today", value: "5" },
          { label: "Saved", value: "3" },
        ],
        path: "/dealhunter_ai/hunt",
        pathLabel: "Open Hunt",
        durationSec: 5,
      },
      {
        navLabel: "Discover",
        pageTitle: "Discover",
        headline: "Browse what the market surfaces",
        narration: "Discover explores broader opportunity lists before you lock into a narrow hunt.",
        layout: "hunt",
        bullets: ["Broader listing feed than Hunt", "Watch markets you care about", "Promote interesting deals to Pipeline"],
        path: "/dealhunter_ai/discover",
        pathLabel: "Open Discover",
        durationSec: 5,
      },
      {
        navLabel: "Deal Map",
        pageTitle: "Deal Map",
        headline: "See deals in geography",
        narration: "Deal Map puts opportunities on a map so location context is clear before diligence.",
        layout: "map",
        bullets: ["Map-first layout with deal pins", "Clusters by market or radius", "Open a pin for listing details"],
        path: "/dealhunter_ai/map",
        pathLabel: "Open Deal Map",
        durationSec: 5,
      },
      {
        navLabel: "My Pipeline",
        pageTitle: "My Pipeline",
        headline: "Track status without losing deals",
        narration: "Move strong candidates into My Pipeline and advance stages as diligence progresses.",
        layout: "pipeline",
        bullets: ["Stage columns for deal progress", "Compass icon in DealHunterIQ nav", "Nothing gets lost in search results"],
        path: "/dealhunter_ai/my_pipeline",
        pathLabel: "Open Pipeline",
        durationSec: 5,
      },
      {
        navLabel: "Analytics",
        pageTitle: "Analytics",
        headline: "Review deal activity trends",
        narration: "Analytics shows how your hunt and pipeline activity is trending over time.",
        layout: "analytics",
        bullets: ["Funnel and pace metrics", "Win-rate style summaries", "Use it to refine your hunt filters"],
        path: "/dealhunter_ai/analytics",
        pathLabel: "Open Analytics",
        durationSec: 5,
      },
    ],
  },
];
